import { ArrowDown } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { EvidenceChip } from "@/components/Evidence";

const Eyebrow = ({ children, light }) => (
  <p className={`text-[0.7rem] uppercase tracking-[0.28em] mb-5 flex items-center gap-3 ${light ? "text-ptf-accent" : "text-ptf-primary/60"}`}>
    <span className={`h-px w-8 inline-block ${light ? "bg-ptf-accent" : "bg-ptf-primary/40"}`} />
    {children}
  </p>
);

const STAGES = [
  { title: "The problem", text: "Cigarette butts are non-biodegradable and highly toxic: one of the most widespread and overlooked forms of litter, damaging the ecosystems they land in." },
  { title: "Field collection", text: "Discarded butts collected from the streets during travel across Dehradun, Lucknow, Bangalore and Goa." },
  { title: "Experimentation", text: "Testing how the collected waste could be cleaned, bound and shaped rather than dumped." },
  { title: "Creative reuse", text: "A handmade surfboard, built from the collected cigarette butts and thermacol: waste turned into a board that rides waves." },
];

export default function ButtToBoard() {
  return (
    <>
      <Seo title="Butt to Board: Innovation Initiative" path="/dawn/butt-to-board" description="Butt to Board collects discarded cigarette butts across Indian cities and repurposes them, including a handmade surfboard built from butts and thermacol." />
      <PageHero
        eyebrow="Innovation initiative"
        titleLines={["Butt", "to board."]}
        intro="Discarded cigarette butts, collected street by street across four cities, and remade into something that rides waves. A reminder that small actions shape the environment."
        image="/expeditions/surfing.jpg"
      />

      <section className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6">
            <Reveal><Eyebrow>The initiative</Eyebrow></Reveal>
            <MaskText
              lines={["Litter, rethought."]}
              className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
            />
            <Reveal delay={0.15}>
              <p className="mt-6 text-lg text-ptf-text/80 leading-relaxed">
                Initiated by Nikhil Pratap Singh, Butt to Board collects discarded
                cigarette butts from the streets during travels across Dehradun,
                Lucknow, Bangalore and Goa. The aim is awareness: cigarette butts are
                non-biodegradable and highly toxic, and this widespread litter damages
                the ecosystems it ends up in.
              </p>
              <p className="mt-5 text-ptf-text/70 leading-relaxed">
                By creatively repurposing the collected butts, the project urges
                responsible disposal, a reminder of how seemingly small actions can
                significantly impact our environment.
              </p>
              <div className="mt-7 flex flex-wrap gap-2" data-testid="b2b-cities">
                {["Dehradun", "Lucknow", "Bangalore", "Goa"].map((c) => (
                  <span key={c} className="text-[0.7rem] uppercase tracking-[0.12em] border border-ptf-primary/25 text-ptf-primary px-3 py-1.5">{c}</span>
                ))}
              </div>
              <p className="mt-6 text-xs text-ptf-text/50 leading-relaxed">
                No scientific claims are made about the resulting material; the board
                is an awareness and innovation piece, documented as built.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            <Reveal><img src="/dawn/b2b-collect.jpg" alt="Collecting cigarette butts from the street" className="w-full aspect-[3/4] object-cover" loading="lazy" /></Reveal>
            <Reveal delay={0.08}><img src="/dawn/b2b-field.jpg" alt="Field collection under Project Dawn" className="w-full aspect-[3/4] object-cover" loading="lazy" /></Reveal>
            <Reveal delay={0.12}><img src="/dawn/b2b-butts.jpg" alt="A bag of collected cigarette butts" className="w-full aspect-[3/4] object-cover" loading="lazy" /></Reveal>
            <Reveal delay={0.16}><img src="/dawn/b2b-boards.jpg" alt="The handmade surfboard built from butts and thermacol" className="w-full aspect-[3/4] object-cover" loading="lazy" /></Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ptf-primary text-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between gap-6 mb-12">
            <h2 className="font-display font-extrabold uppercase text-3xl md:text-5xl tracking-tight leading-[0.95]">From street to surf.</h2>
            <EvidenceChip status="DOCUMENTED" />
          </div>
          <div className="space-y-0">
            {STAGES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.06}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 py-7 border-t border-ptf-bg/15">
                  <div className="md:col-span-1 font-display text-3xl text-ptf-accent">{String(i + 1).padStart(2, "0")}</div>
                  <div className="md:col-span-4 font-display uppercase text-2xl tracking-tight">{s.title}</div>
                  <div className="md:col-span-7 text-ptf-bg/75 leading-relaxed">{s.text}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ptf-secondary text-ptf-bg py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
          <h2 className="font-display font-extrabold uppercase text-4xl md:text-5xl tracking-tight leading-[0.95]">Help take the next<br />board further.</h2>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <PtfButton to="/donate" variant="accent" data-testid="b2b-support-cta">Support the Initiative</PtfButton>
            <PtfButton to="/dawn" variant="outlineLight">Back to Project Dawn</PtfButton>
          </div>
        </div>
      </section>
    </>
  );
}
